import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ChevronsDown } from "lucide-react";

/**
 * CRITICAL REQUIREMENT:
 * 
 * The mobile portrait view (phone in vertical orientation) MUST remain 
 * exactly as it is currently designed and styled.
 * 
 * Any responsive adjustments must ONLY affect:
 * - Landscape orientation (phones on their side)
 * - Tablet devices
 * - Desktop devices
 * 
 * Use media queries that specifically target non-portrait mobile views:
 * - @media (orientation: landscape) { ... }
 * - @media (min-width: 768px) { ... }
 * - @media (min-width: 1024px) { ... }
 */

const NewBanner = () => {
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  useEffect(() => {
    const fetchBannerImage = async () => {
      try {
        console.log("Fetching banner image for section: new-banner", new Date().toISOString());
        const { data, error } = await supabase
          .from("images")
          .select("storage_path, created_at")
          .eq("section", "new-banner")
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (error) {
          console.error("Error fetching banner image:", error);
          toast.error("Fehler beim Laden des Bannerbildes");
          return;
        }

        if (data) {
          console.log("Found banner image:", data);
          const { data: imageData } = await supabase.storage
            .from("images")
            .getPublicUrl(data.storage_path);

          console.log("Image public URL:", imageData.publicUrl);
          setBackgroundImage(imageData.publicUrl + "?t=" + Date.now()); // Add cache-busting parameter
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("Ein unerwarteter Fehler ist aufgetreten");
      }
    };

    fetchBannerImage();
  }, [lastRefresh]); // Include lastRefresh in dependency array

  const scrollToSection2 = () => {
    const section = document.getElementById('new-section-two');
    if (section) {
      const offset = -80;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Function to refresh the banner image
  const refreshBanner = () => {
    setLastRefresh(Date.now());
  };

  // Register event listener for custom event
  useEffect(() => {
    const handleBannerUpload = () => {
      console.log("Banner upload event received, refreshing...");
      refreshBanner();
    };
    
    window.addEventListener("banner-image-uploaded", handleBannerUpload);
    
    return () => {
      window.removeEventListener("banner-image-uploaded", handleBannerUpload);
    };
  }, []);

  return (
    <div className="relative w-full h-screen">
      <div 
        className="absolute inset-0 bg-cover bg-[60%_40%] md:bg-[60%_35%]"
        style={{
          backgroundImage: backgroundImage 
            ? `url('${backgroundImage}')`
            : `url('https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&q=80')`,
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-between items-center">
        <div className="mt-[15vh] md:mt-[15vh]">
          <h1 className="text-4xl md:text-6xl text-white font-bold text-center mb-2">
            EIN GROSSES LEBEN
          </h1>
          <div className="text-lg md:text-2xl text-white font-medium text-center mb-6 tracking-widest">
            COACHING
          </div>
          <p className="text-xl md:text-2xl text-white/90 text-center font-bold max-w-2xl mx-auto">
            Den beruflichen Weg finden, der zu dir passt & auf dem du dein Potenzial voll entfaltest.
          </p>
        </div>
        <div className="mb-12">
          <button 
            onClick={scrollToSection2}
            className="text-white hover:text-white/80 transition-all duration-300"
            aria-label="Scroll to next section"
          >
            <ChevronsDown className="w-12 h-12 animate-bounce" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewBanner;
